// Copyright ©️ 2026 eVolve MEP, LLC

import { addViewerListener } from 'components/Autodesk/extensions/utils';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { THREE } from 'components/Autodesk/viewerThree';
import { isNil, isNotNil } from 'helpers/isNotNil';

import { type HotkeyManager, type SelectionMouseEvent, WINDOW_SELECT_TOOL_NAME } from './types';

const { Autodesk } = window;

type BoxIntersectionSelection = {
  model: Autodesk.Viewing.Viewer3D['model'];
  ids: AutodeskDbId[];
};

/** This represents Autodesk's implementation of BoxIntersection, taken from the default box-selection tool. */
type BoxIntersection = {
  /** Returns the selected elements within the defined box for each model.
   * @param bottomLeft The bottom-left corner of the selection box in viewport coordinates.
   * @param topRight The top-right corner of the selection box in viewport coordinates.
   * @param containmentOnly If true, only elements fully contained within the box are selected. If false, elements that intersect the box are also selected.
   * @returns An array of BoxIntersectionSelection objects, each containing a model and an array of selected element IDs.
   */
  select: (bottomLeft: THREE.Vector3, topRight: THREE.Vector3, containmentOnly: boolean) => BoxIntersectionSelection[];

  [key: string]: unknown;
};

interface BoxSelectionTool extends Autodesk.Viewing.ToolInterface {
  boxIntersection: BoxIntersection;
}

// These classes are styled in `components/Autodesk/forge-viewer.css`.
/** The CSS class shared by the window selection overlay in either drag direction. */
const SELECTION_OVERLAY_CLASS = 'window-selection-overlay';
/** The CSS class for the window selection overlay when selecting fully contained elements. */
const CONTAINMENT_SELECTION_CLASS = 'window-selection-overlay-contains';
/** The CSS class for the window selection overlay when selecting intersecting (i.e. partially contained) elements. */
const INTERSECTION_SELECTION_CLASS = 'window-selection-overlay-intersects';

class WindowSelectTool implements Autodesk.Viewing.ToolInterface {
  active: boolean;
  /** The box intersection logic from Autodesk's box selection tool. */
  boxIntersection: BoxIntersection;
  /** The ending point of the window selection in client coordinates. */
  endPoint: THREE.Vector2;
  /**
   * Used to track which elements need to be highlighted during window selection.
   * Elements that are not already selected but included in the window selection will be highlighted in white.
   * */
  highlightedDbIds: Set<AutodeskDbId>;
  isActivatedByHotkey: boolean;
  isDragging: boolean;
  /** Maps element dbIds to their original colors for restoring after highlighting. */
  mappingDbIdToOriginalColor: Map<AutodeskDbId, THREE.Vector4 | null>;
  selectionOverlayElement: HTMLDivElement;
  /** The starting point of the window selection in client coordinates. */
  startPoint: THREE.Vector2;
  viewer: Autodesk.Viewing.GuiViewer3D;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D, boxIntersection: BoxIntersection) {
    this.viewer = viewer;

    /** The HTML element representing the window selection overlay. */
    this.selectionOverlayElement = document.createElement('div');
    this.selectionOverlayElement.classList.add(SELECTION_OVERLAY_CLASS, CONTAINMENT_SELECTION_CLASS);

    this.active = false;
    this.isActivatedByHotkey = false;

    this.isDragging = false;
    this.highlightedDbIds = new Set();
    this.mappingDbIdToOriginalColor = new Map();

    this.startPoint = new THREE.Vector2();
    this.endPoint = new THREE.Vector2();

    this.boxIntersection = boxIntersection;
  }

  /* oxlint-disable no-empty-function */
  register() {}

  /* oxlint-disable no-empty-function */
  deregister() {}

  activate() {
    this.active = true;
  }

  deactivate() {
    this.active = false;
    this.resetTool();
  }

  getName() {
    return WINDOW_SELECT_TOOL_NAME;
  }

  getNames() {
    return [WINDOW_SELECT_TOOL_NAME];
  }

  getCursor() {
    return 'crosshair';
  }

  update(_timestamp: number) {
    return false;
  }

  // Tools get called in the order of their priority (higher numbers first). Default tools have a priority of 0.
  getPriority() {
    return 99;
  }

  resetTool() {
    this.removeOverlay();
    this.revertHighlightedElementsToOriginalColor(Array.from(this.highlightedDbIds));

    this.isDragging = false;
    this.highlightedDbIds = new Set();
    this.mappingDbIdToOriginalColor = new Map();
  }

  handleGesture(event: SelectionMouseEvent) {
    switch (event.type) {
      case 'dragstart':
        return this.handleButtonDown(event, 0);

      case 'dragmove':
        return this.handleMouseMove(event);

      case 'dragend':
        return this.handleButtonUp(event, 0);

      default:
        break;
    }

    return false;
  }

  // NOTE: The tool controller dispatches 'handleSingleClick' in addition to other handlers like button up/down,
  // so we handle selection logic for everything in handleButtonUp() to avoid double-processing.
  handleSingleClick(_event: SelectionMouseEvent, button: number) {
    const isLeftClick = button === 0;
    if (!isLeftClick) return false;

    // Prevent other controllers from handling the single click.
    return true;
  }

  handleDoubleClick(_event: SelectionMouseEvent, button: number) {
    const isLeftClick = button === 0;
    if (!isLeftClick) return false;

    // Prevent double clicks while the tool is active because the default behavior sets the selection to the double-clicked element only.
    if (this.active) return true;

    return false;
  }

  handleButtonDown(event: SelectionMouseEvent, button: number) {
    const isLeftClick = button === 0;
    if (!isLeftClick) return false;

    this.startPoint.x = event.clientX;
    this.startPoint.y = event.clientY;
    this.endPoint.x = this.startPoint.x;
    this.endPoint.y = this.startPoint.y;
    this.isDragging = true;

    this.createOverlay();

    return true;
  }

  handleMouseMove(event: SelectionMouseEvent) {
    if (!this.isDragging) return false;

    const canvasRect = this.viewer.impl.canvas.getBoundingClientRect();

    // Prevent the overlay from going outside the canvas bounds by clamping the x and y coordinates.
    const clampedX = Math.min(Math.max(event.clientX, canvasRect.left), canvasRect.right);
    const clampedY = Math.min(Math.max(event.clientY, canvasRect.top), canvasRect.bottom);

    this.endPoint.x = clampedX;
    this.endPoint.y = clampedY;

    this.updateOverlay();

    const windowSelectionIds = new Set(this.getWindowSelection());
    const existingSelectionIds = new Set(this.viewer.getSelection());
    const highlightedDbIds = new Set<AutodeskDbId>();

    windowSelectionIds.forEach((id) => {
      if (!existingSelectionIds.has(id)) {
        highlightedDbIds.add(id);
      }
    });

    this.highlightElements(highlightedDbIds);

    return true;
  }

  handleButtonUp(event: SelectionMouseEvent, button: number) {
    const isLeftClick = button === 0;
    if (!isLeftClick) return false;

    this.resetTool();

    const isSingleClick = this.startPoint.x === this.endPoint.x && this.startPoint.y === this.endPoint.y;
    if (isSingleClick) {
      this.handleSingleSelect(event);
    } else {
      this.handleWindowSelect();
    }

    return true;
  }

  handleSingleSelect(event: SelectionMouseEvent) {
    // Find the element clicked that has been clicked. The result will be null if empty space has been clicked.
    const castResult: { dbId?: unknown } | null = this.viewer.clientToWorld(event.canvasX, event.canvasY);
    const selectedElementId = typeof castResult?.dbId === 'number' ? castResult.dbId : null;

    // Do nothing when empty space is selected.
    if (!selectedElementId) return;

    const existingSelection: AutodeskDbId[] = this.viewer.getSelection();
    const updatedSelection = [...existingSelection];

    const existingIndex = existingSelection.findIndex((elementId) => elementId === selectedElementId);
    const wasPreviouslySelected = existingIndex >= 0;

    // Remove the element if it's already been selected.
    if (wasPreviouslySelected) {
      updatedSelection.splice(existingIndex, 1);
    }
    // Otherwise, add the new element to the selection.
    else {
      updatedSelection.push(selectedElementId);
    }

    // Commit the updated selection.
    this.viewer.select(updatedSelection);
  }

  handleWindowSelect() {
    const existingSelection: AutodeskDbId[] = this.viewer.getSelection();
    const windowSelectionIds = this.getWindowSelection();

    const updatedSelection = [...existingSelection, ...windowSelectionIds];
    const uniqueUpdatedSelection = Array.from(new Set(updatedSelection));

    this.viewer.select(uniqueUpdatedSelection);
  }

  getWindowSelection() {
    const canvasRect = this.viewer.impl.getCanvasBoundingClientRect();

    // NOTE: Single clicks are handled in handleSingleClick.
    const isSingleClick = this.startPoint.x === this.endPoint.x && this.startPoint.y === this.endPoint.y;
    if (isSingleClick) return [];

    const bottomLeftVector = this.viewer.impl.clientToViewport(
      Math.min(this.startPoint.x, this.endPoint.x) - canvasRect.left,
      Math.max(this.startPoint.y, this.endPoint.y) - canvasRect.top,
    );

    const topRightVector = this.viewer.impl.clientToViewport(
      Math.max(this.startPoint.x, this.endPoint.x) - canvasRect.left,
      Math.min(this.startPoint.y, this.endPoint.y) - canvasRect.top,
    );

    // Left-to-right drags only select fully contained elements.
    // Right-to-left drags select all elements that intersect the box.
    const containmentOnly = this.endPoint.x >= this.startPoint.x;

    const boxIntersectionResult = this.boxIntersection.select(bottomLeftVector, topRightVector, containmentOnly);
    const modelId = this.viewer.model.id;
    const windowSelectionIds = boxIntersectionResult
      .filter((selection) => selection.model.id === modelId)
      .flatMap((selection) => selection.ids);

    return windowSelectionIds;
  }

  createOverlay() {
    this.viewer.container.appendChild(this.selectionOverlayElement);
    this.updateOverlay();
  }

  updateOverlay() {
    const containerRect = this.viewer.container.getBoundingClientRect();
    const left = Math.min(this.startPoint.x, this.endPoint.x) - containerRect.left;
    const right = Math.max(this.startPoint.x, this.endPoint.x) - containerRect.left;
    const top = Math.min(this.startPoint.y, this.endPoint.y) - containerRect.top;
    const bottom = Math.max(this.startPoint.y, this.endPoint.y) - containerRect.top;

    this.selectionOverlayElement.style.left = left + 'px';
    this.selectionOverlayElement.style.top = top + 'px';
    this.selectionOverlayElement.style.width = right - left + 'px';
    this.selectionOverlayElement.style.height = bottom - top + 'px';

    const isDraggingLeftToRight = this.endPoint.x >= this.startPoint.x;

    // Update the overlay style based on the drag direction.
    if (isDraggingLeftToRight) {
      this.selectionOverlayElement.classList.replace(INTERSECTION_SELECTION_CLASS, CONTAINMENT_SELECTION_CLASS);
    } else {
      this.selectionOverlayElement.classList.replace(CONTAINMENT_SELECTION_CLASS, INTERSECTION_SELECTION_CLASS);
    }
  }

  removeOverlay() {
    if (this.selectionOverlayElement.parentElement) {
      this.selectionOverlayElement.parentElement.removeChild(this.selectionOverlayElement);
    }
  }

  highlightElements(dbIdsToHighlight: Set<AutodeskDbId>) {
    // Get the original colors of the elements to be highlighted.
    dbIdsToHighlight.forEach((dbId) => {
      if (!this.mappingDbIdToOriginalColor.has(dbId)) {
        const originalColor = this.viewer.impl.model.getFragmentList().db2ThemingColor[dbId] ?? null;

        this.mappingDbIdToOriginalColor.set(dbId, originalColor);
      }
    });

    // Revert any previously highlighted elements that are no longer highlighted.
    const dbIdsToRemoveHighlight: AutodeskDbId[] = [];

    this.highlightedDbIds.forEach((dbId) => {
      if (dbIdsToHighlight.has(dbId)) return;

      dbIdsToRemoveHighlight.push(dbId);
    });

    this.revertHighlightedElementsToOriginalColor(dbIdsToRemoveHighlight);

    this.highlightedDbIds = dbIdsToHighlight;

    // White color for highlighting.
    const highlightColor = new THREE.Color(0xffffff);
    const highlightColorVector = new THREE.Vector4(highlightColor.r, highlightColor.g, highlightColor.b, 0.5);

    dbIdsToHighlight.forEach((dbId) => {
      this.viewer.setThemingColor(dbId, highlightColorVector);
    });
  }

  revertHighlightedElementsToOriginalColor(dbIds: AutodeskDbId[]) {
    dbIds.forEach((dbId) => {
      const originalColor = this.mappingDbIdToOriginalColor.get(dbId) ?? null;

      // NOTE: Null restores to the default color, not our custom theme color.
      this.viewer.setThemingColor(dbId, originalColor as THREE.Vector4);
    });
  }
}

class WindowSelectExtension extends Autodesk.Viewing.Extension {
  boxSelectionTool: BoxSelectionTool | null = null;
  tool: WindowSelectTool | null = null;
  cleanUpFns: (() => void)[] = [];

  constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: Autodesk.Viewing.ExtensionOptions) {
    super(viewer, options);

    // NOTE: This may not be available if the BoxSelection extension hasn't been loaded yet.
    const boxSelectionTool = viewer.toolController.getTool('box-selection') as BoxSelectionTool;

    if (isNotNil(boxSelectionTool)) {
      this.replaceBoxSelectionTool(boxSelectionTool);
    }
  }

  load() {
    const handleExtensionLoaded = this.handleExtensionLoaded.bind(this);
    this.cleanUpFns.push(() =>
      addViewerListener(this.viewer, Autodesk.Viewing.EXTENSION_LOADED_EVENT, handleExtensionLoaded),
    );

    return true;
  }

  unload() {
    while (this.cleanUpFns.length > 0) {
      const cleanUpFn = this.cleanUpFns.pop();
      cleanUpFn?.();
    }

    this.viewer.toolController.deactivateTool(WINDOW_SELECT_TOOL_NAME);
    this.viewer.toolController.deregisterTool(this.tool);

    return true;
  }

  handleExtensionLoaded(event: { extensionId: string; type: 'extensionLoaded' }) {
    if (isNotNil(this.boxSelectionTool)) return;

    if (event.extensionId === 'Autodesk.BoxSelection') {
      const boxSelectionTool = this.viewer.toolController.getTool('box-selection') as BoxSelectionTool | undefined;
      // TODO: When changing models, this sometimes occurs. Why?
      if (isNil(boxSelectionTool)) {
        // oxlint-disable-next-line no-console
        console.warn('BoxSelection tool not found after extension loaded.');
        return;
      }
      this.replaceBoxSelectionTool(boxSelectionTool);
    }
  }

  replaceBoxSelectionTool(boxSelectionTool: BoxSelectionTool) {
    // Register the new window selection tool using the box intersection logic from Autodesk's box selection tool.
    this.tool = new WindowSelectTool(this.viewer, boxSelectionTool.boxIntersection);
    this.viewer.toolController.registerTool(this.tool);
    this.registerHotkeys();

    // Remove Autodesk's box selection tool and unload the extension to prevent conflicts.
    this.viewer.toolController.deregisterTool(boxSelectionTool);
    this.viewer.unloadExtension('Autodesk.BoxSelection');
  }

  registerHotkeys() {
    const onPress = () => {
      if (isNil(this.tool)) return true;

      if (!this.tool.active) {
        this.viewer.toolController.activateTool(WINDOW_SELECT_TOOL_NAME);
        this.tool.isActivatedByHotkey = true;
      }

      return true;
    };

    const onRelease = () => {
      if (isNil(this.tool)) return true;

      if (this.tool.isActivatedByHotkey) {
        this.viewer.toolController.deactivateTool(WINDOW_SELECT_TOOL_NAME);
        this.tool.isActivatedByHotkey = false;
      }

      return true;
    };

    const hotkeyManager: HotkeyManager = this.viewer.getHotkeyManager();

    hotkeyManager.pushHotkeys('WindowSelectExtension', [
      {
        keycodes: [Autodesk.Viewing.KeyCode.CONTROL],
        onPress,
        onRelease,
      },
    ]);
  }
}

Autodesk.Viewing.theExtensionManager.registerExtension('WindowSelectExtension', WindowSelectExtension);
